/**
 * Billing Model Helper Utilities
 * 
 * Provides centralized logic for calculating license counts and dimension limits
 * based on the subscription's billing model (PerUser vs FlatRate).
 * 
 * Billing Models:
 * - PerUser: User licenses from Subscriptions.Quantity, other limits from PlanDimensionLimits
 * - FlatRate: All limits (including user count) from PlanDimensionLimits.IncludedQuantity
 * - Hybrid: Mixed model (not commonly used)
 */

/**
 * Get license information based on billing model
 * @param {Object} subscriptionData - Full subscription data from GetSubscriptionByAdmin
 * @returns {Object} License info with total, active, available counts and billing model
 * 
 * @example
 * const licenseInfo = getLicenseInfo(subscriptionData);
 * console.log(licenseInfo.total);      // Total licenses available
 * console.log(licenseInfo.active);     // Currently active licenses
 * console.log(licenseInfo.available);  // Licenses available to assign
 * console.log(licenseInfo.billingModel); // 'PerUser' or 'FlatRate'
 */
export const getLicenseInfo = (subscriptionData) => {
  const users = subscriptionData?.users || [];
  
  // Count active licenses
  const activeLicenses = users.filter(u => 
    u.hasActiveLicense || u.HasActiveLicense
  ).length;

  // Determine billing model (default to PerUser for backwards compatibility)
  const billingModel = subscriptionData?.billingModel || 
                       subscriptionData?.BillingModel || 
                       'PerUser';

  let totalLicenses = 0;
  let purchasedLicenses = 0;

  if (billingModel === 'FlatRate' || billingModel === 'Hybrid') {
    // FlatRate: Get user count from PlanDimensionLimits
    const dimensionLimits = subscriptionData?.dimensionLimits || subscriptionData?.DimensionLimits || [];
    const userCountDimension = dimensionLimits.find(d => 
      (d.dimensionIdentifier || d.DimensionIdentifier) === 'user_count'
    );
    
    // Use IncludedQuantity + PurchasedQuantity for total
    const includedQty = userCountDimension?.includedQuantity || userCountDimension?.IncludedQuantity || 0;
    const purchasedQty = userCountDimension?.purchasedQuantity || userCountDimension?.PurchasedQuantity || 0;
    
    // Check if unlimited
    if (userCountDimension?.isUnlimited || userCountDimension?.IsUnlimited) {
      totalLicenses = 999999; // Effectively unlimited
      purchasedLicenses = 999999; // Show as unlimited
    } else {
      totalLicenses = includedQty + purchasedQty;
      // For FlatRate, "Purchased" shows the total included in the plan (includedQty)
      purchasedLicenses = includedQty;
    }
                    
  } else {
    // PerUser: Use Quantity from Subscriptions table
    totalLicenses = subscriptionData?.quantity || 
                    subscriptionData?.Quantity || 0;
    // For PerUser, "Purchased" shows the subscription quantity
    purchasedLicenses = totalLicenses;
  }

  const availableLicenses = Math.max(0, totalLicenses - activeLicenses);

  return {
    total: totalLicenses,
    purchased: purchasedLicenses,
    active: activeLicenses,
    available: availableLicenses,
    billingModel: billingModel,
    users: users
  };
};

/**
 * Get report dimension limits (count and size)
 * @param {Object} subscriptionData - Full subscription data from GetSubscriptionByAdmin
 * @returns {Object} Report limits including count, size, and current usage
 * 
 * @example
 * const reportLimits = getReportLimits(subscriptionData);
 * console.log(reportLimits.maxReportCount);      // Max reports allowed
 * console.log(reportLimits.maxReportSizeMB);     // Max size per report (MB)
 * console.log(reportLimits.currentReportCount);  // Current number of reports
 * console.log(reportLimits.isUnlimitedReports);  // True if no count limit
 */
export const getReportLimits = (subscriptionData) => {
  const dimensions = subscriptionData?.dimensionLimits || subscriptionData?.DimensionLimits || [];
  
  // Find report_count dimension
  const reportCountDim = dimensions.find(d => 
    (d.dimensionIdentifier || d.DimensionIdentifier) === 'report_count'
  );
  
  // Find max_report_size_mb dimension
  const maxReportSizeDim = dimensions.find(d => 
    (d.dimensionIdentifier || d.DimensionIdentifier) === 'max_report_size_mb'
  );

  // Calculate current report count from workspaces
  const workspaces = subscriptionData?.workspaces || subscriptionData?.Workspaces || [];
  const currentReportCount = workspaces.reduce((count, ws) => {
    const reports = ws.reports || ws.Reports || [];
    return count + reports.length;
  }, 0);

  // Get report count limit
  let maxReportCount = null;
  let isUnlimitedReports = false;
  let purchasedReportCount = 0;
  
  if (reportCountDim) {
    if (reportCountDim.isUnlimited || reportCountDim.IsUnlimited) {
      isUnlimitedReports = true;
      maxReportCount = null; // No limit
      purchasedReportCount = 999999; // Unlimited
    } else {
      const includedQty = reportCountDim.includedQuantity || reportCountDim.IncludedQuantity || 0;
      const purchasedQty = reportCountDim.purchasedQuantity || reportCountDim.PurchasedQuantity || 0;
      maxReportCount = includedQty + purchasedQty;
      // "Purchased" shows the included quantity in the plan
      purchasedReportCount = includedQty;
    }
  }

  // Get report size limit
  let maxReportSizeMB = null;
  let isUnlimitedSize = false;
  let purchasedReportSizeMB = 0;
  
  if (maxReportSizeDim) {
    if (maxReportSizeDim.isUnlimited || maxReportSizeDim.IsUnlimited) {
      isUnlimitedSize = true;
      maxReportSizeMB = null; // No limit
      purchasedReportSizeMB = 999999; // Unlimited
    } else {
      const includedQty = maxReportSizeDim.includedQuantity || maxReportSizeDim.IncludedQuantity || 0;
      const purchasedQty = maxReportSizeDim.purchasedQuantity || maxReportSizeDim.PurchasedQuantity || 0;
      maxReportSizeMB = includedQty + purchasedQty;
      // "Purchased" shows the included quantity in the plan
      purchasedReportSizeMB = includedQty;
    }
  }

  return {
    maxReportCount: maxReportCount,
    maxReportSizeMB: maxReportSizeMB,
    purchasedReportCount: purchasedReportCount,
    purchasedReportSizeMB: purchasedReportSizeMB,
    currentReportCount: currentReportCount,
    remainingReportSlots: maxReportCount !== null ? Math.max(0, maxReportCount - currentReportCount) : null,
    isUnlimitedReports: isUnlimitedReports,
    isUnlimitedSize: isUnlimitedSize,
    isAtReportLimit: maxReportCount !== null && currentReportCount >= maxReportCount
  };
};

/**
 * Check if a file size exceeds the plan's report size limit
 * @param {number} fileSizeBytes - File size in bytes
 * @param {Object} subscriptionData - Full subscription data
 * @returns {Object} Validation result with isValid flag and error message if invalid
 * 
 * @example
 * const validation = validateReportSize(file.size, subscriptionData);
 * if (!validation.isValid) {
 *   alert(validation.errorMessage);
 * }
 */
export const validateReportSize = (fileSizeBytes, subscriptionData) => {
  const reportLimits = getReportLimits(subscriptionData);
  
  // If unlimited size, always valid
  if (reportLimits.isUnlimitedSize || !reportLimits.maxReportSizeMB) {
    return { isValid: true };
  }

  const fileSizeMB = fileSizeBytes / (1024 * 1024);
  
  if (fileSizeMB > reportLimits.maxReportSizeMB) {
    return {
      isValid: false,
      errorMessage: `Report size (${fileSizeMB.toFixed(2)} MB) exceeds your plan limit of ${reportLimits.maxReportSizeMB} MB. Please upgrade your plan or reduce the file size.`,
      fileSizeMB: fileSizeMB,
      limitMB: reportLimits.maxReportSizeMB
    };
  }

  return { isValid: true, fileSizeMB: fileSizeMB, limitMB: reportLimits.maxReportSizeMB };
};

/**
 * Check if adding another report would exceed the plan's report count limit
 * @param {Object} subscriptionData - Full subscription data
 * @returns {Object} Validation result with isValid flag and error message if invalid
 * 
 * @example
 * const validation = validateReportCount(subscriptionData);
 * if (!validation.isValid) {
 *   alert(validation.errorMessage);
 * }
 */
export const validateReportCount = (subscriptionData) => {
  const reportLimits = getReportLimits(subscriptionData);
  
  // If unlimited reports, always valid
  if (reportLimits.isUnlimitedReports || !reportLimits.maxReportCount) {
    return { isValid: true };
  }

  if (reportLimits.isAtReportLimit) {
    return {
      isValid: false,
      errorMessage: `You have reached your plan limit of ${reportLimits.maxReportCount} reports. Please delete a report or upgrade your plan.`,
      currentCount: reportLimits.currentReportCount,
      maxCount: reportLimits.maxReportCount
    };
  }

  return { 
    isValid: true, 
    currentCount: reportLimits.currentReportCount,
    maxCount: reportLimits.maxReportCount,
    remainingSlots: reportLimits.remainingReportSlots
  };
};

/**
 * Get a user-friendly description of the billing model
 * @param {string} billingModel - The billing model ('PerUser', 'FlatRate', 'Hybrid')
 * @returns {string} User-friendly description
 */
export const getBillingModelDescription = (billingModel) => {
  switch (billingModel) {
    case 'PerUser':
      return 'Pay per user - License count determined by your subscription quantity';
    case 'FlatRate':
      return 'Flat rate - Fixed number of licenses included in your plan';
    case 'Hybrid':
      return 'Hybrid billing - Combination of per-user and flat-rate pricing';
    default:
      return 'Standard billing model';
  }
};

/**
 * Format license count for display (handles unlimited case)
 * @param {number} count - License count
 * @param {boolean} isUnlimited - Whether the count is unlimited
 * @returns {string} Formatted string
 */
export const formatLicenseCount = (count, isUnlimited = false) => {
  if (isUnlimited || count >= 999999) {
    return 'Unlimited';
  }
  return count.toString();
};

/**
 * Get license status color based on usage percentage
 * @param {number} active - Active license count
 * @param {number} total - Total license count
 * @returns {string} Color code for UI display
 */
export const getLicenseStatusColor = (active, total) => {
  if (total >= 999999) return '#28a745'; // Green for unlimited
  
  const percentage = (active / total) * 100;
  
  if (percentage >= 100) return '#dc3545';  // Red - at or over limit
  if (percentage >= 80) return '#ffc107';   // Yellow - warning
  return '#28a745';                          // Green - normal
};

/**
 * Get report count status color based on usage percentage
 * @param {number} current - Current report count
 * @param {number} max - Maximum report count
 * @param {boolean} isUnlimited - Whether reports are unlimited
 * @returns {string} Color code for UI display
 */
export const getReportCountStatusColor = (current, max, isUnlimited = false) => {
  if (isUnlimited || !max) return '#28a745'; // Green for unlimited
  
  const percentage = (current / max) * 100;
  
  if (percentage >= 100) return '#dc3545';  // Red - at or over limit
  if (percentage >= 80) return '#ffc107';   // Yellow - warning
  return '#28a745';                          // Green - normal
};
